import { Helmet } from "react-helmet-async";

/**
 * Per-page SEO meta + JSON-LD.
 *
 * Each invocation always renders the full meta surface (title, description,
 * canonical, og:*, twitter:*) so Googlebot — and any social crawler that
 * doesn't execute JS — sees a complete head on every route. The site-wide
 * defaults in apps/web/index.html are overridden per route by react-helmet
 * once the SPA hydrates.
 *
 * `jsonLd` accepts a single block or an array. The `<Seo>` component also
 * injects a default Organization block on every page (suppress with
 * `includeOrganization={false}` if a route needs to opt out).
 */

interface SeoProps {
  /** Page title. Pass it verbatim — the component does NOT auto-append the
   *  studio name anymore. Marketing pages should pass
   *  "X · Vault Breaker Studios"; in-game pages pass just "Cipher Crack". */
  title?: string;
  description?: string;
  path?: string;
  ogImage?: string;
  /** A single JSON-LD object or an array of them (e.g. Organization + FAQPage). */
  jsonLd?: object | object[];
  /** Set to `false` to skip the default Organization block (rare — operator
   *  iframes etc. that don't want studio branding). Defaults to `true`. */
  includeOrganization?: boolean;
}

const DEFAULT_TITLE =
  "Vault Breaker Studios — Provably-Fair Crash Game Provider for Casino Operators";
const DEFAULT_DESCRIPTION =
  "B2B crypto casino game studio. Provably-fair crash, mines, and puzzle originals. Free to integrate, 10% revenue share, weekly USDT/USDC settlement.";
const SITE = "https://vaultbreaker.dev";
const DEFAULT_OG = "/og-image.png";

/** Default Organization JSON-LD attached to every page. */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Vault Breaker Studios",
  url: SITE,
  logo: `${SITE}/logo.svg`,
  sameAs: [
    "https://twitter.com/vaultbreakerstudios",
    "https://www.linkedin.com/company/vault-breaker-studios",
  ],
} as const;

export interface GameSchemaInput {
  /** Display name, e.g. "Vault Breaker". */
  name: string;
  /** Short marketing description used as `description`. */
  description: string;
  /** Optional canonical URL for the game page (absolute or path). */
  url?: string;
  /** Optional hero image URL (absolute or path). */
  image?: string;
}

/**
 * Build a SoftwareApplication / GameApplication JSON-LD block for a game
 * detail page. `aggregateRating` is intentionally omitted — we have no
 * operator deployments to derive ratings from yet.
 */
export function gameSchema(game: GameSchemaInput): object {
  const url = game.url
    ? game.url.startsWith("http")
      ? game.url
      : `${SITE}${game.url}`
    : undefined;
  const image = game.image
    ? game.image.startsWith("http")
      ? game.image
      : `${SITE}${game.image}`
    : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    applicationCategory: "GameApplication",
    name: game.name,
    description: game.description,
    operatingSystem: "Web",
    ...(url ? { url } : {}),
    ...(image ? { image } : {}),
    provider: {
      "@type": "Organization",
      name: "Vault Breaker Studios",
      url: SITE,
    },
  };
}

/**
 * Build a FAQPage JSON-LD block from a list of question/answer pairs.
 * Use for pages with explicit FAQ sections — Docs, Integration, Security.
 */
export function faqSchema(items: { q: string; a: string }[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text: a,
      },
    })),
  };
}

export function Seo({
  title,
  description,
  path = "/",
  ogImage,
  jsonLd,
  includeOrganization = true,
}: SeoProps) {
  // No auto-append. Each page decides whether to carry the studio name.
  // In-game pages (which are operator-iframed) pass bare titles like
  // "Cipher Crack"; marketing pages explicitly include " · Vault Breaker
  // Studios". og:site_name dropped for the same reason — operators don't
  // want our brand surfacing in their iframe's social previews.
  const fullTitle = title ?? DEFAULT_TITLE;
  const desc = description ?? DEFAULT_DESCRIPTION;
  const url = `${SITE}${path}`;
  const rawImage = ogImage ?? DEFAULT_OG;
  // Crawlers (LinkedIn, Slack, some Twitter paths) require absolute URLs.
  const image = rawImage.startsWith("http") ? rawImage : `${SITE}${rawImage}`;

  // Normalize jsonLd into an array, then prepend the default Organization
  // block unless the caller opts out.
  const provided = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];
  const blocks = includeOrganization ? [organizationSchema, ...provided] : provided;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
